import { useEffect, useRef, useState } from 'react';

import { measureAutomaticColumns } from 'utils/table/automaticColumnSizing';
import type { ColumnSizingState } from 'utils/table/columnSizing';
import type { AutomaticColumn, ColumnMeasurementCache } from 'types/table';

export const useAutomaticColumnSizing = (
  columns: readonly AutomaticColumn[],
  onMeasure: (widths: ColumnSizingState, containerWidth: number) => void,
  enabled = columns.length > 0
) => {
  const [element, setElement] = useState<HTMLTableElement | null>(null);
  const current = useRef({ columns, onMeasure });
  const scheduleRef = useRef<() => void>();
  const previousColumns = useRef(columns);

  current.current = { columns, onMeasure };

  useEffect(() => {
    if (!element || !enabled) return;

    let frame: number | undefined;
    let disposed = false;
    let cache: ColumnMeasurementCache = new WeakMap();

    const schedule = () => {
      if (disposed || frame !== undefined) return;
      frame = requestAnimationFrame(() => {
        frame = undefined;
        const latest = current.current;
        const availableWidth = element.parentElement?.clientWidth ?? 0;
        latest.onMeasure(measureAutomaticColumns(element, latest.columns, cache), availableWidth);
      });
    };

    const invalidate = () => {
      cache = new WeakMap();
      schedule();
    };

    scheduleRef.current = schedule;

    const mutations = new MutationObserver((records) => {
      if (
        records.some(({ target }) => {
          const node = target instanceof Element ? target : target.parentElement;
          return node !== element && !node?.closest('colgroup');
        }) ||
        records.some(({ type, target }) => type === 'childList' && target === element)
      )
        schedule();
    });

    mutations.observe(element, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden'],
    });

    let containerWidth: number | undefined;
    const resize =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(([entry]) => {
            if (entry && (containerWidth === undefined || Math.abs(entry.contentRect.width - containerWidth) >= 0.5)) {
              containerWidth = entry.contentRect.width;
              invalidate();
            }
          });

    if (element.parentElement) resize?.observe(element.parentElement);

    window.addEventListener('resize', invalidate);
    element.addEventListener('load', invalidate, true);
    element.addEventListener('input', schedule);
    element.addEventListener('change', schedule);
    document.fonts?.addEventListener('loadingdone', invalidate);

    void document.fonts?.ready.then(() => {
      if (!disposed) invalidate();
    });

    schedule();

    return () => {
      disposed = true;
      if (frame !== undefined) cancelAnimationFrame(frame);
      scheduleRef.current = undefined;
      mutations.disconnect();
      resize?.disconnect();
      window.removeEventListener('resize', invalidate);
      element.removeEventListener('load', invalidate, true);
      element.removeEventListener('input', schedule);
      element.removeEventListener('change', schedule);
      document.fonts?.removeEventListener('loadingdone', invalidate);
    };
  }, [element, enabled]);

  useEffect(() => {
    const previous = previousColumns.current;
    previousColumns.current = columns;
    if (
      previous.length === columns.length &&
      previous.every((column, index) => column.id === columns[index].id && column.headerText === columns[index].headerText)
    )
      return;

    scheduleRef.current?.();
  }, [columns]);

  return setElement;
};
